import { supabase } from '../lib/supabase';
import apiClient from './client';

export interface EmailSetting {
  id: string;
  organization_id: string;
  provider: string;
  client_id?: string;
  tenant_id?: string;
  client_secret?: string;
  from_email?: string;
  from_name?: string;
  is_active: boolean;
  host?: string;
  port?: number;
  security?: string;
  username?: string;
}

export interface EmailTemplate {
  id: string;
  name: string;
  description?: string;
  subject_template: string;
  html_body: string;
  text_body?: string;
  language?: string;
  version?: number;
  is_active: boolean;
  event_name?: string;
  category?: string;
  is_system_template?: boolean;
}

export const emailApi = {
  // Settings
  getEmailSettings: async (organizationId?: string): Promise<EmailSetting | null> => {
    if (supabase) {
      try {
        let query = supabase.from('email_settings').select('*');
        if (organizationId && organizationId !== 'ALL') {
          query = query.eq('organization_id', organizationId);
        }
        const { data, error } = await query.maybeSingle();
        if (!error && data) return data as EmailSetting;
      } catch {
        // Fall through
      }
    }

    try {
      const { data } = await apiClient.get<EmailSetting>('/email/settings', {
        params: { organizationId }
      });
      return data;
    } catch {
      return null;
    }
  },

  saveEmailSettings: async (settings: Partial<EmailSetting>): Promise<EmailSetting> => {
    if (supabase) {
      const { data, error } = await supabase
        .from('email_settings')
        .upsert(settings)
        .select()
        .single();

      if (error) throw error;
      return data as EmailSetting;
    }

    const { data } = await apiClient.post<EmailSetting>('/email/settings', settings);
    return data;
  },

  // Templates
  getEmailTemplates: async (category?: string): Promise<EmailTemplate[]> => {
    if (supabase) {
      try {
        let query = supabase.from('email_templates').select('*').order('name');
        if (category && category !== 'ALL') {
          query = query.eq('category', category);
        }
        const { data, error } = await query;
        if (!error && data) return data as EmailTemplate[];
      } catch {
        // Fall through
      }
    }

    try {
      const { data } = await apiClient.get<EmailTemplate[]>('/email/templates', {
        params: { category }
      });
      return data;
    } catch {
      return [];
    }
  },

  saveEmailTemplate: async (template: Partial<EmailTemplate>): Promise<EmailTemplate> => {
    if (supabase) {
      const { data, error } = await supabase
        .from('email_templates')
        .upsert(template)
        .select()
        .single();

      if (error) throw error;
      return data as EmailTemplate;
    }

    if (template.id && !template.id.startsWith('tpl-')) {
      const { data } = await apiClient.put<EmailTemplate>(`/email/templates/${template.id}`, template);
      return data;
    }
    const { data } = await apiClient.post<EmailTemplate>('/email/templates', template);
    return data;
  },

  deleteEmailTemplate: async (id: string): Promise<{ success: boolean }> => {
    if (supabase) {
      const { error } = await supabase.from('email_templates').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    }

    const { data } = await apiClient.delete<{ success: boolean }>(`/email/templates/${id}`);
    return data;
  },

  // Logs
  getEmailLogs: async (organizationId?: string, limit = 50): Promise<any[]> => {
    if (supabase) {
      try {
        let query = supabase.from('email_logs').select('*').order('created_at', { ascending: false }).limit(limit);
        if (organizationId && organizationId !== 'ALL') {
          query = query.eq('organization_id', organizationId);
        }
        const { data, error } = await query;
        if (!error && data) return data;
      } catch {
        // Fall through
      }
    }

    try {
      const { data } = await apiClient.get<any[]>('/email/logs', {
        params: { organizationId, limit }
      });
      return data;
    } catch {
      return [];
    }
  },

  // Routing Rules
  getRoutingRules: async (organizationId?: string): Promise<any[]> => {
    if (supabase) {
      try {
        let query = supabase.from('email_routing_rules').select('*');
        if (organizationId && organizationId !== 'ALL') {
          query = query.eq('organization_id', organizationId);
        }
        const { data, error } = await query;
        if (!error && data) return data;
      } catch {
        // Fall through
      }
    }

    try {
      const { data } = await apiClient.get<any[]>('/email/routing-rules', {
        params: { organizationId }
      });
      return data;
    } catch {
      return [];
    }
  },

  createRoutingRule: async (rule: any): Promise<any> => {
    if (supabase) {
      const { data, error } = await supabase
        .from('email_routing_rules')
        .insert(rule)
        .select()
        .single();

      if (error) throw error;
      return data;
    }

    const { data } = await apiClient.post<any>('/email/routing-rules', rule);
    return data;
  },

  updateRoutingRule: async (id: string, updates: any): Promise<any> => {
    if (supabase) {
      const { data, error } = await supabase
        .from('email_routing_rules')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    }

    const { data } = await apiClient.put<any>(`/email/routing-rules/${id}`, updates);
    return data;
  },

  deleteRoutingRule: async (id: string): Promise<{ success: boolean }> => {
    if (supabase) {
      const { error } = await supabase.from('email_routing_rules').delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    }

    const { data } = await apiClient.delete<{ success: boolean }>(`/email/routing-rules/${id}`);
    return data;
  }
};
